import {
  HeaderController,
  BIRDModelController,
  HeaderLocation as BirdHeaderLocation,
} from '@sas-dvr/ltjs-bird';
import { merge, Observable, of, from } from 'rxjs';
import {
  BirdLayoutState,
  PromptConstraints,
} from '@sas-dvr/internal-va-react-core/store/birdLayout/BirdLayoutState';
import { filterNulls, getKey } from '../util';
import { Action, AnyAction } from 'redux';
import { mergeMap } from 'rxjs/operators';
import { EpicActions } from '../actions/epicActions';
import {
  HeaderLocation,
  PromptHeaderState,
} from '@sas-dvr/internal-va-react-core/store/promptHeader/promptHeaderState';
import { FlexibilityType, DimensionUnit } from '@sas-dvr/ltjs-commons';
import { BirdEpicDependencies } from '../epics';

export const loadHeaderController = (
  controller: HeaderController | null,
  unload$: Observable<BIRDModelController>,
  id: string,
  deps: BirdEpicDependencies
): Observable<AnyAction> => {
  if (!controller) {
    return of(PromptHeaderState.actions.init(id, { location: 'top', status: 'auto' }));
  }
  const mediaContainerController = controller.getMediaContainerController();
  if (!mediaContainerController) {
    return of(PromptHeaderState.actions.init(id, { location: 'top', status: 'auto' }));
  }
  const childControllers = filterNulls(
    mediaContainerController.getOrderedVisualAndContainerControllers()
  );

  const layoutActions: Action[] = [];
  for (const controller of childControllers) {
    const constraint = controller.getLayoutProxy()?.getConstraint();
    if (!constraint) {
      continue;
    }
    const promptLayout: PromptConstraints = {
      widthFlexibility: convertFlexibility(constraint.getWidthFlexibility()),
      heightFlexibility: convertFlexibility(constraint.getHeightFlexibility()),
    };
    const width = constraint.getWidth();
    if (width) {
      if (width.getUnit() !== DimensionUnit.PERCENTAGE) {
        console.warn('Unexpected dimension unit for prompt header prompt: ', width?.getUnit());
      } else {
        promptLayout.widthConstraint = width.getValue() + '%';
      }
    }
    const height = constraint.getHeight();
    if (height) {
      if (height.getUnit() !== DimensionUnit.PERCENTAGE) {
        console.warn('Unexpected dimension unit for prompt header prompt: ', width?.getUnit());
      } else {
        promptLayout.heightConstraint = height.getValue() + '%';
      }
    }
    layoutActions.push(
      BirdLayoutState.actions.setPromptConstraints(deps.getKey(controller), promptLayout)
    );
  }

  const location = convertHeaderLocation(controller.getLocation());
  return merge(
    from(layoutActions),
    of(
      PromptHeaderState.actions.init(id, {
        status: 'auto',
        location,
        width: controller.getModel()?.hasPropertyOverride('width')
          ? controller.getModel()?.getWidth() || undefined
          : undefined,
      })
    ),
    from(childControllers.map(EpicActions.actions.loadBirdModel)),
    unload$.pipe(mergeMap(() => from(childControllers.map(EpicActions.actions.unloadBirdModel)))),
    of(BirdLayoutState.actions.set(id, { childKeys: childControllers.map(getKey) })),
    from(layoutActions)
  );
};

function convertFlexibility(flexibility: FlexibilityType) {
  switch (flexibility) {
    case FlexibilityType.FLEXIBLE:
      return 'flexible';
    case FlexibilityType.GROWABLE:
      return 'growable';
    case FlexibilityType.SHRINKABLE:
      return 'shrinkable';
    case FlexibilityType.FIXED:
      return 'fixed';
  }
}

function convertHeaderLocation(headerLocation: BirdHeaderLocation): HeaderLocation {
  switch (headerLocation) {
    case BirdHeaderLocation.LEFT:
      return 'left';
    case BirdHeaderLocation.RIGHT:
      return 'right';
    case BirdHeaderLocation.BOTTOM:
      return 'bottom';
    case BirdHeaderLocation.TOP:
    default:
      return 'top';
  }
}
